import { Buffer } from 'buffer';
import { Connection, PublicKey, SystemProgram, Transaction } from '@solana/web3.js';
import { createTransferCheckedInstruction, getAssociatedTokenAddress, TOKEN_PROGRAM_ID } from '@solana/spl-token';

export type AwesomeConfig = {
  cluster: 'devnet' | 'mainnet-beta';
  rpcUrl: string;
  tokenMint: string;
  treasuryWallet: string;
  voteCostUi: number;
  tokenDecimals: number;
  mode: 'demo' | 'solana';
};

export const AWESOME_CONFIG: AwesomeConfig = {
  cluster: (import.meta.env.VITE_SOLANA_CLUSTER as 'devnet' | 'mainnet-beta') || 'devnet',
  rpcUrl: import.meta.env.VITE_SOLANA_RPC_URL || 'https://api.devnet.solana.com',
  tokenMint: import.meta.env.VITE_AWESOME_MINT || '',
  treasuryWallet: import.meta.env.VITE_AWESOME_TREASURY || '',
  voteCostUi: Number(import.meta.env.VITE_AWESOME_VOTE_COST || '10'),
  tokenDecimals: Number(import.meta.env.VITE_AWESOME_DECIMALS || '6'),
  mode: (import.meta.env.VITE_AWESOME_MODE as 'demo' | 'solana') || 'demo'
};

export type SolanaProvider = {
  isPhantom?: boolean;
  publicKey?: PublicKey;
  connect: () => Promise<{ publicKey: PublicKey }>;
  signTransaction: (transaction: Transaction) => Promise<Transaction>;
};

declare global {
  interface Window {
    solana?: SolanaProvider;
  }
}

export function getProvider() {
  return window.solana?.isPhantom ? window.solana : window.solana;
}

export function voteCostRaw(config = AWESOME_CONFIG) {
  return BigInt(Math.round(config.voteCostUi * 10 ** config.tokenDecimals));
}

export async function buildVotePaymentTransaction(params: {
  voter: PublicKey;
  winnerId: string;
  loserId: string;
  nonce: string;
  config?: AwesomeConfig;
}) {
  const config = params.config || AWESOME_CONFIG;
  if (!config.tokenMint || !config.treasuryWallet) throw new Error('Missing AWESOME mint/treasury config');

  const connection = new Connection(config.rpcUrl, 'confirmed');
  const mint = new PublicKey(config.tokenMint);
  const treasury = new PublicKey(config.treasuryWallet);
  const voterAta = await getAssociatedTokenAddress(mint, params.voter, false, TOKEN_PROGRAM_ID);
  const treasuryAta = await getAssociatedTokenAddress(mint, treasury, true, TOKEN_PROGRAM_ID);
  const latest = await connection.getLatestBlockhash('confirmed');
  const memo = `AWESOME_VOTE:${params.winnerId}:${params.loserId}:${params.nonce}`;

  const tx = new Transaction({
    feePayer: params.voter,
    blockhash: latest.blockhash,
    lastValidBlockHeight: latest.lastValidBlockHeight
  });

  tx.add(
    createTransferCheckedInstruction(
      voterAta,
      mint,
      treasuryAta,
      params.voter,
      voteCostRaw(config),
      config.tokenDecimals,
      [],
      TOKEN_PROGRAM_ID
    )
  );
  tx.add(
    new Transaction().add({
      keys: [],
      programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'),
      data: Buffer.from(memo, 'utf8')
    }).instructions[0]
  );
  tx.add(
    SystemProgram.transfer({
      fromPubkey: params.voter,
      toPubkey: params.voter,
      lamports: 0
    })
  );

  return { tx, connection, memo };
}

export async function requestVoteNonce(wallet: string, winnerId: string, loserId: string) {
  const res = await fetch('/api/vote/nonce', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ wallet, winnerId, loserId })
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<{ nonce: string; expiresAt: number }>;
}

export async function submitVerifiedVote(payload: { wallet: string; winnerId: string; loserId: string; nonce: string; signature: string }) {
  const res = await fetch('/api/vote/confirm', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<{ things: import('./things').Thing[]; delta: number; matchup?: MatchupStats; message: string }>;
}

export type MatchupStats = {
  a_id: string;
  b_id: string;
  a_wins: number;
  b_wins: number;
  votes: number;
  last_winner_id: string;
  last_winner_name: string;
};

export async function submitDemoVote(payload: { winnerId: string; loserId: string; sessionId: string }) {
  const res = await fetch('/api/vote/demo', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<{ things: import('./things').Thing[]; delta: number; matchup?: MatchupStats; message: string }>;
}

export async function fetchArena(count = 160) {
  const res = await fetch(`/api/arena?count=${count}&t=${Date.now()}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<{ things: import('./things').Thing[]; source: string }>;
}
